import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { auth } from '../../services/firebase';
import { paydlesApi } from '../../services/api';
import { CreatePaydleDto } from '../../types/paydle';

interface CreatePaydleFormProps {
  onSuccess?: (paydleId: string, shareId: string, accessToken: string) => void;
  onCancel?: () => void;
}

export const CreatePaydleForm: React.FC<CreatePaydleFormProps> = ({ onSuccess, onCancel }) => {
  const [word, setWord] = useState('');
  const [price, setPrice] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate word
    if (word.length !== 5) {
      setError('Word must be exactly 5 letters');
      return;
    }

    if (!/^[a-zA-Z]+$/.test(word)) {
      setError('Word must contain only letters');
      return;
    }

    // Validate price
    const priceNum = parseFloat(price);
    if (isNaN(priceNum) || priceNum <= 0) {
      setError('Price must be a positive number');
      return;
    }

    setLoading(true);

    try {
      const user = auth.currentUser;
      const userId = user?.uid || `anon_${Date.now()}`;
      const userName = user?.displayName || user?.email || 'Anonymous';

      const paydleData: CreatePaydleDto = {
        word: word.toUpperCase(),
        price: priceNum,
        description,
        creatorId: userId,
        creatorName: userName,
      };

      const response = await paydlesApi.create(paydleData);

      if (response.data) {
        const { id, shareId, accessToken } = response.data;
        onSuccess?.(id, shareId, accessToken);
      }
    } catch (err: any) {
      console.error('Error creating Paydle:', err);
      setError(err.response?.data?.error || 'Failed to create Paydle');
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-lg shadow-lg p-6 max-w-md mx-auto"
    >
      <h2 className="text-2xl font-bold mb-6 text-gray-800">Create a Paydle</h2>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label htmlFor="word" className="block text-gray-700 font-medium mb-2">
            5-Letter Word *
          </label>
          <input
            type="text"
            id="word"
            value={word}
            onChange={e => setWord(e.target.value.toUpperCase())}
            maxLength={5}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent uppercase text-center text-2xl font-bold tracking-widest"
            placeholder="ENTER"
            required
          />
          <p className="text-sm text-gray-500 mt-1">
            The word players will try to guess
          </p>
        </div>

        <div className="mb-4">
          <label htmlFor="price" className="block text-gray-700 font-medium mb-2">
            Price (if they lose) *
          </label>
          <div className="relative">
            <span className="absolute left-3 top-2 text-gray-500 text-lg">$</span>
            <input
              type="number"
              id="price"
              value={price}
              onChange={e => setPrice(e.target.value)}
              step="0.01"
              min="0.01"
              className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="10.00"
              required
            />
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Amount players must pay if they fail to guess
          </p>
        </div>

        <div className="mb-6">
          <label htmlFor="description" className="block text-gray-700 font-medium mb-2">
            Description (optional)
          </label>
          <textarea
            id="description"
            value={description}
            onChange={e => setDescription(e.target.value)}
            rows={3}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Add a hint or message for players..."
          />
        </div>

        <div className="flex gap-3">
          <button
            type="submit"
            disabled={loading}
            className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Creating...' : 'Create Paydle'}
          </button>

          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
          )}
        </div>
      </form>
    </motion.div>
  );
};
